/**
 * PHARMAKON — LoginPage (design-system port)
 *
 * Ported from the design's pages/auth/LoginPage.jsx. Submit is wired to
 * the existing real-auth flow (useAuthStore.login → POST /auth/login)
 * per scope answer #3. The design's "Démo accès rapide" mock-role buttons
 * are removed (scope #4 — no mock data) and replaced with the existing
 * register / fournisseur portal links.
 */
import * as React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  Button,
  Checkbox,
  Field,
  Icon,
  Input,
  Wordmark,
} from "@/components/ui/primitives";
import { BRAND } from "@/config/brand.config.js";
import { API_BASE_URL } from "@/config/app.config.js";
import { useAuthStore } from "@/stores/authStore.js";
import { useThemeStore } from "@/stores/themeStore.js";
import { THEME_OPTIONS } from "@/config/ui.config.js";
import { enterBeta } from "@/mock/beta-mode.js";
import { isSupplierRole } from "@/config/fournisseur.constants.js";
// X XWP-72 — the entry of the shared team login (CP §15.3): « Connexion équipe », or the picker when
// this browser already holds an open workstation.
import { MESSAGES_SESSION_EQUIPE, ROUTES_SESSION_EQUIPE } from "@/config/session-equipe.config.js";
import { lirePoste, posteOuvert } from "@/lib/session-equipe.js";
import "@/styles/session-equipe.css";

// Role-aware post-login landing path. Suppliers (the 7 granular FOURNISSEUR_*
// roles + legacy FOURNISSEUR/LABORATOIRE) land on the supplier portal; pharmacy
// and cashier redirects are unchanged (compatibility).
const homeForRole = (role) => {
  if (role === "ADMIN_PHARMAKON") return "/admin/dashboard";
  if (role === "CAISSIER") return "/caissier";
  if (isSupplierRole(role)) return "/portail-fournisseur";
  return "/dashboard";
};

// Compact number formatter for the marketing counters (e.g. 2.4 K, 1.2 M)
const fmtCount = (n) =>
  n == null
    ? "—"
    : new Intl.NumberFormat("fr-DZ", {
        notation: "compact",
        maximumFractionDigits: 1,
      }).format(n);

const Stat = ({ label, value, hint }) => (
  <div>
    <div className="text-2xs uppercase tracking-wider text-text-muted font-semibold mb-1">
      {label}
    </div>
    <div className="text-2xl font-semibold tracking-tight text-text-strong kpi-num">
      {value}
    </div>
    <div className="text-2xs text-text-muted mt-0.5">{hint}</div>
  </div>
);

export default function LoginPage() {
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [showPwd, setShowPwd] = React.useState(false);
  const [capabilityIdx, setCapabilityIdx] = React.useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const messageEtat = location.state?.message ?? null;
  const [poste] = React.useState(() => (posteOuvert() ? lirePoste() : null));
  const [stats, setStats] = React.useState({
    pharmacies: null,
    transactions: null,
  });
  const login = useAuthStore((s) => s.login);
  const loading = useAuthStore((s) => s.loading);
  const errorFromStore = useAuthStore((s) => s.error);
  const [error, setError] = React.useState(null);

  const themePref = useThemeStore((s) => s.themePref);
  const setTheme = useThemeStore((s) => s.setTheme);

  React.useEffect(() => {
    const t = setInterval(
      () => setCapabilityIdx((i) => (i + 1) % BRAND.capabilityLines.length),
      3200,
    );
    return () => clearInterval(t);
  }, []);

  // Public platform counters for the marketing panel. Counts only (no PII),
  // served unauthenticated at <API>/stats/public. Must use the absolute API
  // base URL — a relative "/api/v1/..." would hit the SPA host, not the server.
  React.useEffect(() => {
    let alive = true;
    fetch(`${API_BASE_URL}/stats/public`)
      .then((res) => res.json())
      .then((d) => {
        if (!alive) return;
        const data = d?.data ?? d ?? {};
        setStats({
          pharmacies: data.pharmacies ?? null,
          transactions: data.transactions ?? null,
        });
      })
      .catch(() => {
        /* keep placeholders if the API is unreachable */
      });
    return () => {
      alive = false;
    };
  }, []);
  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const user = await login(email, password);
      if (user) navigate(homeForRole(user.role));
    } catch (err) {
      setError(
        err?.response?.data?.message ??
          err?.message ??
          "Identifiants invalides. Vérifiez l'adresse e-mail et le mot de passe.",
      );
    }
  };

  const displayError = error || errorFromStore;

  // ── BETA "Try Before Purchase" entry ──────────────────────────────────────
  // Mounts the in-memory mock backend, plants a fake user in the auth store,
  // and lands the user on the role-specific dashboard. All subsequent API
  // calls are intercepted by the mock adapter (see src/mock/adapter.js).
  const enterBetaAs = (role) => {
    setError(null);
    try {
      const user = enterBeta(role);
      // Push the fake user into the live auth store so RBAC, sidebar, and
      // topbar render correctly without needing a full reload.
      useAuthStore.setState({ user, loading: false, error: null });
      const home = role === "CAISSIER" ? "/caissier" : "/dashboard";
      navigate(home);
    } catch (e) {
      setError(
        e?.message ?? "Impossible de démarrer la démonstration. Réessayez.",
      );
    }
  };

  // Three demo personas the prospective customer can step into. Kept short
  // and brand-neutral — the visual chrome (color, badge) is supplied by the
  // card component below, not the data, to keep the section feeling like a
  // commercial product picker rather than a stylised marketing block.
  const BETA_ROLES = [
    {
      role: "PROPRIETAIRE",
      label: "Propriétaire",
      sub: "Vue 360°",
      icon: "shield-check",
    },
    {
      role: "MANAGER",
      label: "Manager",
      sub: "Opérations",
      icon: "layout-dashboard",
    },
    {
      role: "CAISSIER",
      label: "Caissier",
      sub: "Point de vente",
      icon: "scan-barcode",
    },
  ];

  return (
    <div
      className="min-h-screen grid grid-cols-1 md:grid-cols-[1.05fr_1fr]"
      style={{ background: "var(--color-bg)" }}
    >
      {/* LEFT — brand canvas */}
      <aside className="relative hidden md:flex flex-col justify-between p-10 lg:p-12 brand-canvas-bg overflow-hidden">
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.06]"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern
              id="grid"
              width="32"
              height="32"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 32 0 L 0 0 0 32"
                fill="none"
                stroke="currentColor"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>

        <div className="relative">
          <Wordmark size="lg" />
        </div>

        <div className="relative max-w-xl">
          <div className="inline-flex items-center gap-2 mb-5 text-2xs uppercase tracking-widest text-accent font-semibold">
            <span className="w-6 h-px bg-accent"></span>
            Édition pharmacie · Algérie
          </div>
          <h1
            className="text-4xl font-semibold tracking-tight text-text-strong leading-tight mb-3"
            style={{ letterSpacing: "-0.025em" }}
          >
            {BRAND.tagline}
          </h1>
          <p className="text-lg text-text-muted leading-relaxed mb-8">
            {BRAND.positioning}
          </p>

          <div className="h-7 relative overflow-hidden">
            {BRAND.capabilityLines.map((line, i) => (
              <div
                key={i}
                className={cn(
                  "absolute inset-0 flex items-center gap-2.5 transition-all duration-500",
                  i === capabilityIdx
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 -translate-y-2",
                )}
              >
                <Icon name="check" size={14} className="text-accent" />
                <span className="text-sm text-text-default">{line}</span>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-border-subtle">
            <Stat
              label="Pharmacies"
              value={fmtCount(stats.pharmacies)}
              hint="actives en Algérie"
            />
            <Stat
              label="Transactions"
              value={fmtCount(stats.transactions)}
              hint="ventes traitées"
            />
            <Stat label="Disponibilité" value="99.97%" hint="SLA garanti" />
          </div>
        </div>

        <div className="relative text-2xs text-text-muted font-mono">
          {BRAND.copyright}
        </div>
      </aside>

      {/* RIGHT — form */}
      <section className="flex flex-col justify-center px-6 py-12 lg:px-16 relative">
        <div className="absolute top-6 right-6 flex items-center gap-1.5 p-1 bg-bg-sunken rounded-md border border-border-subtle">
          {THEME_OPTIONS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTheme(t.value)}
              aria-label={`Thème : ${t.label}`}
              aria-pressed={themePref === t.value}
              title={`Thème : ${t.label}`}
              className={cn(
                "w-8 h-8 rounded inline-flex items-center justify-center transition-colors",
                themePref === t.value
                  ? "bg-bg-elevated text-text-strong shadow-subtle"
                  : "text-text-muted hover:text-text-default",
              )}
            >
              <Icon name={t.icon} size={16} />
            </button>
          ))}
        </div>

        <div className="md:hidden mb-8">
          <Wordmark size="md" />
        </div>

        <div className="w-full max-w-sm mx-auto lg:mx-0">
          <h2 className="text-2xl font-semibold tracking-tight text-text-strong mb-1.5">
            Connexion
          </h2>
          <p className="text-sm text-text-muted mb-8">
            Espace pharmacie sécurisé
          </p>

          {displayError && (
            <div className="mb-5 flex items-start gap-2.5 px-3.5 py-2.5 bg-danger-soft border border-danger/30 rounded-md">
              <Icon
                name="alert-circle"
                size={14}
                className="text-danger mt-0.5 shrink-0"
              />
              <div className="text-xs text-danger">{displayError}</div>
            </div>
          )}
          {messageEtat && !displayError && (
            <div role="status" className="mb-5 text-sm text-text-muted">
              {messageEtat}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <Field label="Adresse e-mail" required>
              <Input
                type="email"
                name="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                icon="mail"
                placeholder="vous@votre-pharmacie.dz"
                size="lg"
                required
              />
            </Field>

            <Field label="Mot de passe" required>
              <div className="relative">
                <Input
                  type={showPwd ? "text" : "password"}
                  name="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  icon="lock"
                  size="lg"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  aria-label={
                    showPwd
                      ? "Masquer le mot de passe"
                      : "Afficher le mot de passe"
                  }
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-default transition-colors"
                >
                  <Icon name={showPwd ? "eye-off" : "eye"} size={15} />
                </button>
              </div>
            </Field>

            <div className="flex items-center justify-between text-xs">
              <Checkbox label="Rester connecté" />
              <button
                type="button"
                onClick={() => navigate("/forgot-password")}
                className="text-accent hover:underline whitespace-nowrap"
              >
                Mot de passe oublié ?
              </button>
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              loading={loading}
              iconRight={loading ? null : "arrow-right"}
            >
              {loading ? "Connexion…" : "Se connecter"}
            </Button>
          </form>

          {/* X XWP-72 — the shared workstation of the officine (owner's team login + code, then the picker) */}
          <section className="pk-session-equipe-entree" aria-labelledby="pk-entree-equipe-titre">
            <h3 id="pk-entree-equipe-titre" className="pk-session-equipe-entree-titre">
              {MESSAGES_SESSION_EQUIPE.entree.titre}
            </h3>
            <p className="pk-session-equipe-note">
              {poste?.session?.libelle_poste
                ? MESSAGES_SESSION_EQUIPE.entree.posteOuvert(poste.session.libelle_poste)
                : MESSAGES_SESSION_EQUIPE.entree.aide}
            </p>
            <div className="pk-session-equipe-entree-actions">
              {poste && (
                <Link to={ROUTES_SESSION_EQUIPE.CHOIX} className="btn primary">
                  <Icon name="users" size={16} />
                  {MESSAGES_SESSION_EQUIPE.entree.choisir}
                </Link>
              )}
              <Link to={ROUTES_SESSION_EQUIPE.CONNEXION} className="btn secondary">
                <Icon name="monitor" size={16} />
                {MESSAGES_SESSION_EQUIPE.entree.bouton}
              </Link>
            </div>
          </section>

          <div className="my-7 flex items-center gap-3 text-2xs text-text-muted">
            <span className="flex-1 h-px bg-border-subtle"></span>
            <span className="uppercase tracking-wider">ou</span>
            <span className="flex-1 h-px bg-border-subtle"></span>
          </div>

          {/* ── Évaluation produit — accès démo (horizontal, premium) ── */}
          <section
            aria-labelledby="beta-heading"
            className="mb-6 rounded-lg border border-border-subtle bg-bg-elevated/60 p-4"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-1.5 mb-1.5 text-2xs uppercase tracking-wider text-accent font-semibold">
                  <span
                    aria-hidden="true"
                    className="w-1.5 h-1.5 rounded-full bg-accent"
                  />
                  Évaluation gratuite
                </div>
                <h3
                  id="beta-heading"
                  className="text-sm font-semibold text-text-strong leading-tight"
                >
                  Tester la plateforme sans inscription
                </h3>
                <p className="text-2xs text-text-muted mt-1 leading-relaxed">
                  Choisissez un rôle pour explorer l'environnement complet avec
                  un jeu de données pharmacie réaliste.
                </p>
              </div>
              <span
                className="hidden sm:inline-flex shrink-0 items-center gap-1 px-1.5 py-0.5 rounded border border-border-subtle bg-bg-sunken text-text-muted text-[10px] font-mono tracking-wider"
                title="Aucun compte ni paiement requis"
              >
                BETA
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {BETA_ROLES.map((r) => (
                <button
                  key={r.role}
                  type="button"
                  onClick={() => enterBetaAs(r.role)}
                  aria-label={`Démarrer la démo en tant que ${r.label}`}
                  className={cn(
                    "group relative flex flex-col items-center justify-center gap-1.5 rounded-md",
                    "border border-border-subtle bg-bg-elevated px-2 py-3",
                    "hover:border-accent hover:bg-accent-soft/30",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40",
                    "transition-all duration-150",
                  )}
                >
                  <span
                    className={cn(
                      "inline-flex items-center justify-center w-8 h-8 rounded-md",
                      "bg-bg-sunken text-text-default border border-border-subtle",
                      "group-hover:bg-accent group-hover:text-accent-fg group-hover:border-accent",
                      "transition-colors duration-150",
                    )}
                  >
                    <Icon name={r.icon} size={15} />
                  </span>
                  <span className="text-xs font-semibold text-text-strong leading-none">
                    {r.label}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-text-muted leading-none">
                    {r.sub}
                  </span>
                </button>
              ))}
            </div>
          </section>

          <div className="my-6 flex items-center gap-3 text-2xs text-text-muted">
            <span className="flex-1 h-px bg-border-subtle"></span>
            <span className="uppercase tracking-wider">Inscription</span>
            <span className="flex-1 h-px bg-border-subtle"></span>
          </div>

          <div className="space-y-2.5">
            <Link to="/register" className="block">
              <Button
                variant="secondary"
                size="lg"
                icon="building-2"
                className="w-full justify-start"
              >
                Nouvelle pharmacie ? Créer un compte
              </Button>
            </Link>
            <Link to="/register-fournisseur" className="block">
              <Button
                variant="secondary"
                size="lg"
                icon="package"
                className="w-full justify-start"
              >
                Espace fournisseur
              </Button>
            </Link>
          </div>

          {/* ── Footer · collaborator + Novera signature + legal ───── */}
          <footer
            className="mt-8 pt-5 border-t border-border-subtle"
            role="contentinfo"
          >
            {/* Row 1: Collaborator */}
            <div className="flex items-center justify-between py-3 border-b border-border-subtle">
              <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold min-w-[80px]">
                En collaboration
              </span>
              <a
                href="https://siahmed-parapharm.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2.5 opacity-80 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/collaborateur/siahmed-logo.png"
                  alt=""
                  aria-hidden="true"
                  className="h-6 w-6 rounded-md object-contain border border-border-subtle"
                />
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs font-medium text-text-strong leading-none">
                    Siahmed Parapharm
                  </span>
                  <span className="text-[10px] font-mono text-text-muted leading-none">
                    siahmed-parapharm.com
                  </span>
                </div>
              </a>
            </div>

            {/* Row 2: Developer */}
            <div className="flex items-center justify-between py-3 border-b border-border-subtle">
              <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold min-w-[80px]">
                Developed by
              </span>
              <a
                href="https://novera-dz.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-2.5 opacity-80 hover:opacity-100 transition-opacity"
              >
                <img
                  src="/novera.svg"
                  alt=""
                  aria-hidden="true"
                  className="h-5 w-auto object-contain"
                />
                <span className="text-[10px] font-mono text-text-muted">
                  novera-dz.com
                </span>
              </a>
            </div>

            {/* Row 3: Legal + Copyright */}
            <div className="flex items-center justify-between pt-3">
              <nav
                className="flex items-center gap-2.5"
                aria-label="Liens légaux"
              >
                <Link
                  to="/terms"
                  className="text-[11px] text-text-muted hover:text-text-default hover:underline underline-offset-2 transition-colors"
                >
                  Conditions générales
                </Link>
                <span
                  aria-hidden="true"
                  className="w-1 h-1 rounded-full bg-border-default"
                />
                <Link
                  to="/privacy"
                  className="text-[11px] text-text-muted hover:text-text-default hover:underline underline-offset-2 transition-colors"
                >
                  Confidentialité
                </Link>
                <span
                  aria-hidden="true"
                  className="w-1 h-1 rounded-full bg-border-default"
                />
                <Link
                  to="/legal"
                  className="text-[11px] text-text-muted hover:text-text-default hover:underline underline-offset-2 transition-colors"
                >
                  Mentions légales
                </Link>
              </nav>
              <span className="text-[10px] font-mono text-text-muted">
                {BRAND.copyright}
              </span>
            </div>
          </footer>
        </div>
      </section>
    </div>
  );
}
